import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Chip,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow
} from '@nextui-org/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { listAgencyOrphans, purgeDeletedAgency, purgeSummary } from '../lib/deleteAgency';

// Restos de agencias que ya no existen: ids que siguen apareciendo en el
// tracking (D1), en el almacenamiento (R2), en colecciones que solo escribe el
// Worker o en licencias de creadores, pero sin doc en agencies/. Borrar una
// agencia ya limpia todo esto (cf-worker › api/backofficeAgencyPurge.js); aquí
// quedan las borradas antes de oct-2026 o con una limpieza a medias. La lista
// es una prueba en seco: no se borra nada hasta pulsar «Limpiar».

const WHERE_LABEL = {
  'd1:agency_tracked_creators': 'Tracking',
  'd1:agency_live_sessions': 'Directos',
  'd1:agency_live_battles': 'Batallas',
  'r2:private/agency-backstage/': 'Backstage',
  'r2:agencies/': 'Archivos',
  'users:agencyLicenseBy': 'Licencias de creadores'
};
const whereLabel = (w) => WHERE_LABEL[w] || w.replace(/^firestore:/, '');

export default function AgenciaRestos() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState('');
  const [done, setDone] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setItems(await listAgencyOrphans());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function clean(ids) {
    const list = ids.join(', ');
    if (!confirm(`¿Limpiar todo lo que queda de ${list}?\n\nSe retiran las licencias de sus creadores (reanudando los cobros que pausó la agencia), y se borran su Backstage, archivos, tracking y avisos.\n\nNo se puede deshacer.`)) return;
    for (const id of ids) {
      setBusy(id);
      const res = await purgeDeletedAgency(id);
      setDone((d) => ({ ...d, [id]: res }));
    }
    setBusy('');
    await load();
  }

  const ids = items.map((o) => o.agencyId);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button className="text-sm text-default-500 hover:underline" onClick={() => navigate('/agencias')}>← Agencias</button>
          <h1 className="text-2xl font-semibold">Restos de agencias borradas</h1>
          <p className="text-sm text-default-500">
            Datos de agencias que ya no existen: licencias de sus creadores, Backstage, archivos, tracking y avisos. Al borrar una agencia ahora se limpia todo; aquí quedan las de antes. La lista no borra nada hasta que pulses «Limpiar».
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="flat" onPress={load} isDisabled={loading || !!busy}>Recargar</Button>
          {canWrite && ids.length > 1 && (
            <Button color="danger" variant="flat" onPress={() => clean(ids)} isDisabled={loading || !!busy}>Limpiar todas</Button>
          )}
        </div>
      </div>

      {error && <div className="rounded-md border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{error}</div>}

      {Object.entries(done).map(([id, res]) => (
        <div key={id} className={`rounded-md border p-3 text-sm ${res.error || res.report?.errors?.length ? 'border-warning-200 bg-warning-50 text-warning-800' : 'border-success-200 bg-success-50 text-success-800'}`}>
          <strong>{id}</strong>: {res.error
            ? `no se pudo limpiar (${res.error})`
            : `limpiado · ${purgeSummary(res.report)}${res.report?.errors?.length ? ` · algo falló: ${res.report.errors[0]}` : ''}`}
        </div>
      ))}

      <Table aria-label="Restos de agencias borradas" removeWrapper>
        <TableHeader>
          <TableColumn>AGENCIA (ID)</TableColumn>
          <TableColumn>DÓNDE QUEDA</TableColumn>
          <TableColumn>SE BORRARÍA</TableColumn>
          <TableColumn>ACCIÓN</TableColumn>
        </TableHeader>
        <TableBody
          isLoading={loading}
          loadingContent={<Spinner label="Buscando restos… (puede tardar un poco)" />}
          emptyContent={loading ? ' ' : 'No queda nada de agencias borradas.'}
        >
          {items.map((o) => {
            const r = o.report || {};
            const pending = [];
            if (r.users?.licensesRetired) pending.push(<Chip key="lic" size="sm" color="warning" variant="flat">{r.users.licensesRetired} con licencia</Chip>);
            if (r.errors?.length) pending.push(<Chip key="err" size="sm" color="danger" variant="flat">{r.errors.length} errores al revisar</Chip>);
            return (
              <TableRow key={o.agencyId}>
                <TableCell><span className="font-mono">{o.agencyId}</span></TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {o.where.map((w) => <Chip key={w} size="sm" variant="flat">{whereLabel(w)}</Chip>)}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    <span className="text-sm">{purgeSummary(r)}</span>
                    {pending.length > 0 && <div className="flex flex-wrap gap-1">{pending}</div>}
                  </div>
                </TableCell>
                <TableCell>
                  {canWrite ? (
                    <Button size="sm" color="danger" variant="flat" isLoading={busy === o.agencyId} isDisabled={!!busy} onPress={() => clean([o.agencyId])}>
                      Limpiar
                    </Button>
                  ) : '—'}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
